import type { ITuningClock } from '../contracts/ports';

export class TuningTrialError extends Error {
  constructor(readonly code: string, message: string) { super(message); this.name = 'TuningTrialError'; }
}

/** One host-owned deadline and cancellation token, shared by every awaited stage. */
export class TrialOperation {
  readonly deadlineMs: number;
  readonly startedAtMs: number;
  private readonly controller = new AbortController();

  constructor(private readonly clock: ITuningClock, budgetMs = 15_000) {
    if (!Number.isFinite(budgetMs) || budgetMs <= 0) throw new RangeError('Invalid trial budget');
    this.startedAtMs = clock.monotonicMs(); this.deadlineMs = this.startedAtMs + budgetMs;
  }

  get signal(): AbortSignal { return this.controller.signal; }
  remainingMs(): number { return Math.max(0, this.deadlineMs - this.clock.monotonicMs()); }
  active(): boolean { return !this.signal.aborted && this.remainingMs() > 0; }
  assertActive(): void {
    if (this.signal.aborted) throw new TuningTrialError('cancelled', 'Trial operation was cancelled');
    if (!this.remainingMs()) throw new TuningTrialError('trial-timeout', 'Trial operation exceeded its deadline');
  }
  cancel(reason = 'cancelled'): void { if (!this.signal.aborted) this.controller.abort(reason); }

  async within<T>(work: () => Promise<T>, stage: string, stageBudgetMs?: number): Promise<T> {
    this.assertActive();
    const waitMs = Math.min(this.remainingMs(), stageBudgetMs ?? Infinity);
    if (waitMs <= 0) { this.cancel('deadline'); throw new TuningTrialError('trial-timeout', `${stage} exceeded its trial deadline`); }
    const stageDeadlineMs = this.clock.monotonicMs() + waitMs;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let abort: (() => void) | undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => { reject(new TuningTrialError('trial-timeout', `${stage} exceeded its trial deadline`)); this.cancel('deadline'); }, waitMs);
      abort = () => reject(new TuningTrialError('cancelled', `${stage} was cancelled`));
      this.signal.addEventListener('abort', abort, { once: true });
    });
    try {
      const value = await Promise.race([Promise.resolve().then(() => { this.assertActive(); return work(); }), deadline]);
      if (this.clock.monotonicMs() >= stageDeadlineMs) {
        this.cancel('deadline'); throw new TuningTrialError('trial-timeout', `${stage} exceeded its trial deadline`);
      }
      this.assertActive(); return value;
    } finally {
      if (timer) clearTimeout(timer);
      if (abort) this.signal.removeEventListener('abort', abort);
    }
  }
}
